import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import Button from "../Button";
import Callout from "../Callout";

function SignParentConsentForm() {
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const { code } = useParams();
  const navigate = useNavigate();

  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState("");
  const [editedPdfUrl, setEditedPdfUrl] = useState("");
  const [isSigning, setIsSigning] = useState(false);
  const [originalBlob, setOriginalBlob] = useState(null);

  useEffect(() => {
    const fetchPDF = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINT}/api/sign-parent-form/${code}/`);
        const fullUrl = `${API_ENDPOINT}${response.data.pdf}`;
        const blob = await fetch(fullUrl).then((res) => res.blob());
        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
        setOriginalBlob(blob);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching parent form:", err);
        setLoading(false);
      }
    };

    fetchPDF();
  }, [API_ENDPOINT, code]);

  const handleSignDocument = async () => {
    if (!signature) {
      alert("Please provide your full name as signature.");
      return;
    }

    setIsSigning(true);
    try {
      const now = new Date();
      const timestamp = now.toLocaleDateString();

      const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const italicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
      const page = pdfDoc.getPages()[0];

    // Parent name (printed)
    page.drawText(signature, {
        x: 177,
        y: 190,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
    });
    
    // Parent signature
    page.drawText(signature, {
        x: 372,
        y: 190,
        size: 14,
        font: italicFont,
        color: rgb(0, 0, 0),
    });
    
    // Date
    page.drawText(timestamp, {
        x: 487,
        y: 190,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
    });

      const editedPdfBytes = await pdfDoc.save();
      const blob = new Blob([editedPdfBytes], { type: "application/pdf" });
      const editedUrl = URL.createObjectURL(blob);
      setEditedPdfUrl(editedUrl);

      const formData = new FormData();
      formData.append("pdf", blob, "parent_signed_form.pdf");

      await axios.put(`${API_ENDPOINT}/api/sign-parent-form/${code}/`, formData);

      navigate("/sign-up");
    } catch (error) {
      console.error("Error signing parent form:", error);
      alert("There was a problem signing the form.");
    }
    setIsSigning(false);
  };

  if (loading) return <p>Loading the Parent Consent Form...</p>;

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "1000px",
        margin: "0 auto",
        textAlign: "left",
      }}
    >
      <h1>Review and Sign Your Child’s Consent Form</h1>
      <Callout text="Please review the consent form below. This form allows your child to take college-level courses while in high school. By signing this document, you confirm your understanding and permission for your child to participate in dual enrollment." />

      {!editedPdfUrl && (
        <div style={{ overflowX: "auto" }}>
        <iframe
          src={pdfUrl}
          title="Original PDF Preview"
          style={{
            width: "100%",
            minWidth: "320px",
            height: "70vh",
            border: "1px solid #ccc",
          }}
        />
      </div>
      )}

      <div style={{ textAlign: "left", marginTop: "40px" }}>
        <h2 style={{ marginBottom: "10px" }}>Adopt Your Signature</h2>
        <p>Confirm your name and provide your signature to complete this form.</p>

        <label style={{ display: "block", marginBottom: "10px" }}>
          <strong>Full Name</strong>
        </label>
        <input
          type="text"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="Enter your full name"
          style={{
            width: "100%",
            padding: "10px",
            fontSize: "16px",
            marginBottom: "30px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        <label style={{ display: "block", marginBottom: "10px" }}>
          <strong>Preview Signature</strong>
        </label>
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "20px",
            marginBottom: "30px",
            minHeight: "140px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Times New Roman, serif",
            fontStyle: "italic",
            fontSize: "28px",
            backgroundColor: "#fafafa",
          }}
        >
          {signature || "Your signature preview"}
        </div>

        <div
          style={{
            padding: "20px",
            backgroundColor: "#f4f4f4",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          <p style={{ fontSize: "14px", marginBottom: "20px" }}>
            By clicking Adopt and Sign, you are electronically signing this document to give your child permission to participate in dual enrollment opportunities.
          </p>
          <Button
            onClick={handleSignDocument}
            disabled={isSigning}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: isSigning ? "not-allowed" : "pointer",
            }}
          >
            {isSigning ? "Signing..." : "Adopt and Sign"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SignParentConsentForm;